import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Loader,
  toast,
} from "@devhop/ui";
import { formatDateTimeShort } from "@/shared/lib/date-time";
import { CheckCircle2, RefreshCw } from "lucide-react";
import type { Refund } from "../../domain/types";
import { useCompleteRefund } from "../api/queries";

type RefundListProps = {
  refunds: Refund[];
  branchId: string | null;
  onRefundUpdated?: () => void;
};

export function RefundList({
  refunds,
  branchId,
  onRefundUpdated,
}: RefundListProps) {
  const completeRefundMutation = useCompleteRefund();

  const handleCompleteRefund = async (refundId: string) => {
    if (!branchId) {
      toast.error("Branch ID is required");
      return;
    }

    try {
      await completeRefundMutation.mutateAsync({
        refundId,
        branchId,
      });
      toast.success("ສຳເລັດ refund ແລ້ວ");
      onRefundUpdated?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການສຳເລັດ refund",
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "bg-yellow-500/10 text-yellow-600";
      case "APPROVED":
        return "bg-blue-500/10 text-blue-600";
      case "COMPLETED":
        return "bg-green-500/10 text-green-600";
      case "REJECTED":
        return "bg-red-500/10 text-red-600";
      case "FAILED":
        return "bg-gray-500/10 text-gray-600";
      default:
        return "bg-gray-500/10 text-gray-600";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "REQUESTED":
        return "ລໍຖ້າອະນຸມັດ";
      case "APPROVED":
        return "ອະນຸມັດແລ້ວ";
      case "REJECTED":
        return "ປະຕິເສດ";
      case "COMPLETED":
        return "ສຳເລັດແລ້ວ";
      case "FAILED":
        return "ລົ້ມເຫລວ";
      default:
        return status;
    }
  };

  const getReasonLabel = (reasonCode: string) => {
    switch (reasonCode) {
      case "CUSTOMER_REQUEST":
        return "ລູກຄ້າຂໍຄືນເງິນ";
      case "ITEM_ISSUE":
        return "ສິນຄ້າມີບັນຫາ";
      case "PAYMENT_ERROR":
        return "ຂໍ້ຜິດພາດການຊຳລະ";
      case "OTHER":
        return "ອື່ນໆ";
      default:
        return reasonCode;
    }
  };

  if (refunds.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <RefreshCw className="h-8 w-8 text-muted-foreground" />
          </EmptyMedia>
          <EmptyTitle>ບໍ່ມີ Refund</EmptyTitle>
          <EmptyDescription>ບໍ່ມີ refund requests ສຳລັບ order ນີ້</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="font-medium text-sm">ປະຫວັດການຄືນເງິນ</h3>
      {refunds.map((refund) => (
        <div
          key={refund.id}
          className="flex items-center justify-between rounded-md border p-3"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(refund.status)}>
                {getStatusLabel(refund.status)}
              </Badge>
              <span className="font-medium text-sm">
                {Number(refund.amountGross).toLocaleString("lo-LA")} ₭
              </span>
            </div>
            <div className="mt-1 text-muted-foreground text-xs">
              <span>{getReasonLabel(refund.reasonCode)}</span>
              {refund.createdAt && (
                <>
                  <span className="mx-2">•</span>
                  <span>
                    {formatDateTimeShort(refund.createdAt)}
                  </span>
                </>
              )}
            </div>
          </div>
          {refund.status === "APPROVED" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCompleteRefund(refund.id)}
              disabled={completeRefundMutation.isPending}
            >
              {completeRefundMutation.isPending ? (
                <Loader />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              <span className={completeRefundMutation.isPending ? "ml-2" : ""}>
                ສຳເລັດ
              </span>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
